import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { QUIZMASTER_API } from '../config';
import '../styles/styles.css';

const ResultsPage = ({ accessToken, supabaseRefreshToken, onSignIn }) => {
    const [quizData, setQuizData] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [recommendations, setRecommendations] = useState([]);
    const location = useLocation();
    const navigate = useNavigate();
    const instanceId = location.state?.selectedInstanceId;
    const document = location.state?.selectedDocument;
    const handleReturnToQuizzes = useCallback(() => {
        if (document) {
            navigate('/quiz-list', { state: { selectedDocument: document } });
        } else {
            navigate('/profile');
        }
    }, [document, navigate]);

    const refreshToken = useCallback(async () => {
        try {
            const refreshResponse = await fetch(`${QUIZMASTER_API}/auth/refresh`, {
                method: 'POST',
                headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: supabaseRefreshToken }),
            });
            if (!refreshResponse.ok) throw new Error('Token refresh failed');
            const { access_token: newAccessToken, refresh_token: newRefreshToken } = await refreshResponse.json();
            onSignIn({ accessToken: newAccessToken, refreshToken: newRefreshToken });
            return newAccessToken;
        } catch (err) {
            throw new Error('Session expired. Please sign in again.');
        }
    }, [supabaseRefreshToken, onSignIn]);

    const fetchWithAuth = useCallback(async (url, options) => {
        let response = await fetch(url, {
            ...options,
            headers: { ...options.headers, 'Authorization': `Bearer ${accessToken}`, 'Accept': 'application/json' },
        });
        if (response.status === 401 && supabaseRefreshToken) {
            const newAccessToken = await refreshToken();
            response = await fetch(url, {
                ...options,
                headers: { ...options.headers, 'Authorization': `Bearer ${newAccessToken}`, 'Accept': 'application/json' },
            });
        }
        return response;
    }, [accessToken, supabaseRefreshToken, refreshToken]);

    useEffect(() => {
        if (!instanceId) {
            setError('No quiz instance selected.');
            setLoading(false);
            return;
        }

        const fetchQuizInstanceResults = async () => {
            try {
                const response = await fetchWithAuth(
                    `${QUIZMASTER_API}/get-quiz-instance-details/?instance_id=${instanceId}`,
                    {}
                );
                if (!response.ok) throw new Error('Failed to fetch quiz instance results');
                const data = await response.json();
                const formattedQuestions = data.questions.map((question) => ({
                    ...question,
                    submitted_response_option_id: question.submitted_response_option_id || null,
                    correct_answer_explanation: question.correct_answer_explanation || 'No explanation available',
                }));
                setQuizData({
                    quiz_status: data.quiz_status,
                    quiz_type: data.quiz_type,
                    questions: formattedQuestions,
                });
                if (accessToken) {
                    const recResponse = await fetchWithAuth(
                        `${QUIZMASTER_API}/review-recommendations/${instanceId}`,
                        {}
                    );
                    if (recResponse.ok) {
                        const recData = await recResponse.json();
                        setRecommendations(recData.recommendations || []);
                    }
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchQuizInstanceResults();
    }, [instanceId, fetchWithAuth, accessToken]);

    const handleQuestionSelect = (index) => {
        setCurrentQuestionIndex(index);
    };

    const handleRecommendations = () => {
        if (quizData) {
            setCurrentQuestionIndex(quizData.questions.length);
        }
    };

    const metrics = useMemo(() => {
        if (!quizData || !Array.isArray(quizData.questions)) {
            return { total: 0, answered: 0, correct: 0, score: 0 };
        }

        const total = quizData.questions.length;
        let answered = 0;
        let correct = 0;

        quizData.questions.forEach((question) => {
            if (question.submitted_response_option_id !== null) {
                answered += 1;
                const answer = question.response_options.find(
                    (option) => option.id === question.submitted_response_option_id
                );
                if (answer?.is_correct) {
                    correct += 1;
                }
            }
        });

        const score = total > 0 ? Math.round((correct / total) * 100) : 0;
        return { total, answered, correct, score };
    }, [quizData]);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading...</p>
            </div>
        );
    }

    if (error || !instanceId) {
          return (
              <div className="error-container">
                  <p>{error || 'No quiz instance selected.'}</p>
                  <button className="button confirm-button" onClick={handleReturnToQuizzes}>
                      Back to Quizzes
                  </button>
              </div>
          );
    }

    if (!quizData || quizData.questions.length === 0) {
          return (
              <div className="container">
                  <h2 className="sub-header">No results found for this quiz instance.</h2>
                  <button className="button confirm-button" onClick={handleReturnToQuizzes}>
                      Back to Quizzes
                  </button>
              </div>
          );
    }

    const totalQuestions = metrics.total;
    const isRecommendationsView = currentQuestionIndex === totalQuestions;
    const currentQuestion = isRecommendationsView ? null : quizData.questions[currentQuestionIndex];
    const userAnswer = currentQuestion
        ? currentQuestion.response_options.find((option) => option.id === currentQuestion.submitted_response_option_id)
        : null;
    const correctAnswer = currentQuestion
        ? currentQuestion.response_options.find((option) => option.is_correct)
        : null;

    return (
        <div className="scroll-container quiz-page quiz-page--results">
            <div className="quiz-page__header">
                <div className="quiz-page__titles">
                    <h1 className="quiz-page__title">{document?.filename ? `${document.filename} Quiz Results` : 'Quiz Results'}</h1>
                    <p className="quiz-page__subtitle">Review your performance and note the sections to revisit.</p>
                </div>
                <div className="quiz-page__meta-group">
                    <div className="quiz-status-pill" aria-live="polite">
                        <span className="quiz-status-pill__label">Score</span>
                        <span className="quiz-status-pill__value">{metrics.score}%</span>
                    </div>
                    <div className="quiz-status-pill" aria-live="polite">
                        <span className="quiz-status-pill__label">Answered</span>
                        <span className="quiz-status-pill__value">{metrics.answered} / {totalQuestions}</span>
                    </div>
                    <div className="quiz-status-pill" aria-live="polite">
                        <span className="quiz-status-pill__label">Correct</span>
                        <span className="quiz-status-pill__value">{metrics.correct}</span>
                    </div>
                    {instanceId && (
                        <div className="quiz-status-pill quiz-status-pill--muted" title="Quiz instance identifier">
                            <span className="quiz-status-pill__label">Instance</span>
                            <span className="quiz-status-pill__value quiz-status-pill__value--truncate">{instanceId}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="quiz-progress" aria-hidden="true">
                <div className="quiz-progress__bar">
                    <div className="quiz-progress__fill" style={{ width: `${metrics.score}%` }}></div>
                </div>
            </div>

            <div className="question-navigation question-navigation--elevated">
                {quizData.questions.map((question, index) => {
                    const questionAnswer = question.response_options.find((option) => option.id === question.submitted_response_option_id);
                    const isCorrect = questionAnswer?.is_correct;
                    return (
                        <button
                            key={question.question_id || index}
                            type="button"
                            className={`number-button ${currentQuestionIndex === index ? 'number-button-active' : ''}`}
                            onClick={() => handleQuestionSelect(index)}
                            title={`View question ${index + 1}`}
                        >
                            <span className="number-text">{index + 1}</span>
                            {question.submitted_response_option_id !== null && (
                                <span className={`number-indicator ${isCorrect ? 'number-indicator--correct' : 'number-indicator--incorrect'}`}></span>
                            )}
                        </button>
                    );
                })}
                <button
                    type="button"
                    className={`number-button ${isRecommendationsView ? 'number-button-active' : ''}`}
                    onClick={handleRecommendations}
                    title="View recommendations"
                >
                    <span className="number-text">R</span>
                </button>
            </div>
            {isRecommendationsView ? (
                <article className="quiz-question-card quiz-question-card--results">
                    <header className="quiz-question-card__header">
                        <p className="quiz-question-card__eyebrow">Guided next steps</p>
                        <h2 className="quiz-question-card__title">Recommendations</h2>
                    </header>
                    {recommendations.length > 0 ? (
                        <ul className="recommendations-list">
                            {recommendations.map((rec, index) => (
                                <li key={`${rec.section}-${index}`} className="recommendation-item">
                                    <span className="recommendation-item__badge">{rec.section}</span>
                                    <span className="recommendation-item__text">{rec.reason}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="quiz-question-card__helper quiz-question-card__helper--muted">No personalised recommendations available.</p>
                    )}
                </article>
            ) : (
                <article className="quiz-question-card quiz-question-card--results" aria-live="polite">
                    <header className="quiz-question-card__header">
                        <p className="quiz-question-card__eyebrow">Question {currentQuestionIndex + 1}</p>
                        <h2 className="quiz-question-card__title">{currentQuestion.question_text}</h2>
                    </header>
                    <div className="quiz-question-card__content">
                        {currentQuestion.submitted_response_option_id !== null ? (
                            <div className={`quiz-answer ${userAnswer?.is_correct ? 'quiz-answer--success' : 'quiz-answer--error'}`}>
                                <span className="quiz-answer__label">Your answer</span>
                                <span className="quiz-answer__value">{userAnswer?.response_text || 'Not available'}</span>
                                <span className="quiz-answer__state">{userAnswer?.is_correct ? 'Correct' : 'Incorrect'}</span>
                            </div>
                        ) : (
                            <span className="quiz-question-card__helper quiz-question-card__helper--muted">You did not answer this question.</span>
                        )}
                        <div className="quiz-answer quiz-answer--reference">
                            <span className="quiz-answer__label">Correct answer</span>
                            <span className="quiz-answer__value">{correctAnswer?.response_text || 'Not available'}</span>
                        </div>
                        <div className="quiz-answer quiz-answer--explanation">
                            <span className="quiz-answer__label">Explanation</span>
                            <span className="quiz-answer__value">{currentQuestion.correct_answer_explanation}</span>
                        </div>
                    </div>
                </article>
            )}
            <div className="quiz-page__actions">
                  <button
                      type="button"
                      className="button button--ghost quiz-page__back"
                      onClick={handleReturnToQuizzes}
                  >
                    Back to Quizzes
                </button>
            </div>
        </div>
    );
};

export default ResultsPage;